import {
  isAdvertisingConsentGranted as realIsAdvertisingConsentGranted,
  onConsentChange as realOnConsentChange,
  whenTrustArcReady as realWhenTrustArcReady
} from './consent-gate'

export namespace MetaPixel {
  export type Event =
    | 'AddPaymentInfo'
    | 'PageView'
    | 'Purchase'
    | 'InitiateCheckout'
    | 'AddToCart'
    | 'CustomizeProduct'
    | 'ViewContent'
    | 'Contact'
    | 'Lead'
    | 'FindLocation'

  export type EventPayload<E extends Event> = EventPayloadMap[E]

  type Content = Record<string, any>
  type CurrencyAndAmount = { currency: string; value: number }
  // Destination-catalog geo params per the marketing-supplied Meta scripts.
  // Sent as-is from stored address data, which is inconsistent (mixed
  // abbreviations / full names); catalog matching keys on content_ids, not these.
  type Geo = { city?: string; region?: string; country?: string }

  type AddPaymentInfoPayload = {
    content_ids?: (string | number)[]
    contents?: Content[]
  } & Partial<CurrencyAndAmount>
  type PurchasePayload = {
    content_ids?: (string | number)[]
    content_type?: string
    contents?: Content[]
    num_items?: number
  } & CurrencyAndAmount &
    Geo
  type InitiateCheckoutPayload = {
    content_ids?: (string | number)[]
    content_type?: string
    contents?: Content[]
    num_items?: number
  } & Partial<CurrencyAndAmount> &
    Geo
  type AddToCartPayload = {
    content_ids?: (string | number)[]
    content_type?: string
    contents?: Content[]
  } & Partial<CurrencyAndAmount>
  type ViewContentPayload = { contents?: Content[] }

  interface EventPayloadMap {
    AddPaymentInfo: AddPaymentInfoPayload
    PageView: {}
    Purchase: PurchasePayload
    InitiateCheckout: InitiateCheckoutPayload
    AddToCart: AddToCartPayload
    CustomizeProduct: {}
    ViewContent: ViewContentPayload
    Contact: {}
    Lead: {}
    FindLocation: {}
  }
}

export type MetaPixelTrack = <E extends MetaPixel.Event>(event: E, payload: MetaPixel.EventPayload<E>) => void

export type MetaPixelSnapshot = { track: MetaPixelTrack }

export type MetaPixelStore = {
  subscribe: (subscriber: () => void) => () => void
  getSnapshot: () => MetaPixelSnapshot
  getServerSnapshot: () => MetaPixelSnapshot
}

// Injectable side effects. Default singleton wires real implementations
// (DOM, fbq, consent-gate). Tests inject mocks.
export type MetaPixelLoaderDeps = {
  injectFbScript: () => void
  initPixel: (pixelId: string) => void
  setConsent: (action: 'grant' | 'revoke') => void
  track: <E extends MetaPixel.Event>(pixelId: string, event: E, payload: MetaPixel.EventPayload<E>) => void
  removeFbScript: () => void
  isAdvertisingConsentGranted: () => boolean
  whenTrustArcReady: (cb: () => void) => () => void
  onConsentChange: (cb: () => void) => () => void
}

const NOOP_SNAPSHOT: MetaPixelSnapshot = { track: () => void 0 }
const NOOP_STORE: MetaPixelStore = {
  subscribe: () => () => {},
  getSnapshot: () => NOOP_SNAPSHOT,
  getServerSnapshot: () => NOOP_SNAPSHOT
}

export function createMetaPixelLoader(deps: MetaPixelLoaderDeps) {
  type PixelEntry = {
    refCount: number
    snapshot: MetaPixelSnapshot
  }
  const pixels = new Map<string, PixelEntry>()
  // fbq's pixel state lives in window and persists across script-tag teardowns and React
  // remounts, so we track which IDs we've already passed to fbq('init', ...) and never
  // re-init the same ID (which otherwise produces a "Duplicate Pixel ID" warning).
  const initializedPixels = new Set<string>()
  let scriptLoaded = false
  let consentCleanup: (() => void) | null = null
  let pollCleanup: (() => void) | null = null
  let totalSubscribers = 0

  function initPixelIfNeeded(pixelId: string) {
    if (initializedPixels.has(pixelId)) return
    initializedPixels.add(pixelId)
    deps.initPixel(pixelId)
  }

  function loadScriptAndInitAll() {
    if (!scriptLoaded) {
      deps.injectFbScript()
      scriptLoaded = true
    }
    for (const pixelId of pixels.keys()) {
      initPixelIfNeeded(pixelId)
    }
  }

  function handleConsentChange() {
    if (deps.isAdvertisingConsentGranted()) {
      loadScriptAndInitAll()
      deps.setConsent('grant')
    } else if (scriptLoaded) {
      deps.setConsent('revoke')
    }
  }

  function ensureGlobalSubscription() {
    if (totalSubscribers !== 1) return
    pollCleanup = deps.whenTrustArcReady(() => {
      if (deps.isAdvertisingConsentGranted()) {
        loadScriptAndInitAll()
      }
    })
    consentCleanup = deps.onConsentChange(handleConsentChange)
  }

  function tearDownGlobalSubscription() {
    if (totalSubscribers !== 0) return
    if (scriptLoaded) {
      deps.removeFbScript()
      scriptLoaded = false
    }
    consentCleanup?.()
    consentCleanup = null
    pollCleanup?.()
    pollCleanup = null
  }

  function getOrCreateEntry(pixelId: string): PixelEntry {
    const existing = pixels.get(pixelId)
    if (existing) return existing
    const entry: PixelEntry = {
      refCount: 0,
      snapshot: {
        track: (event, payload) => deps.track(pixelId, event, payload)
      }
    }
    pixels.set(pixelId, entry)
    return entry
  }

  function registerPixel(pixelId: string | null | undefined): MetaPixelStore {
    if (!pixelId || pixelId === 'none') return NOOP_STORE
    const entry = getOrCreateEntry(pixelId)

    return {
      subscribe(_subscriber) {
        entry.refCount++
        totalSubscribers++

        const isFirstForThisPixel = entry.refCount === 1
        const scriptAlreadyLoaded = scriptLoaded
        ensureGlobalSubscription()
        if (isFirstForThisPixel && scriptAlreadyLoaded && deps.isAdvertisingConsentGranted()) {
          initPixelIfNeeded(pixelId)
        }
        // If script not yet loaded, init will happen in loadScriptAndInitAll.

        return () => {
          entry.refCount--
          totalSubscribers--
          if (entry.refCount === 0) {
            pixels.delete(pixelId)
          }
          tearDownGlobalSubscription()
        }
      },
      getSnapshot: () => entry.snapshot,
      getServerSnapshot: () => NOOP_SNAPSHOT
    }
  }

  return { registerPixel }
}

function makeDefaultDeps(): MetaPixelLoaderDeps {
  let scriptTag: HTMLScriptElement | null = null

  return {
    injectFbScript: () => {
      if (scriptTag) return
      scriptTag = document.createElement('script')
      scriptTag.innerHTML = /* js */ `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
      `
      scriptTag.async = true
      document.head.append(scriptTag)
    },
    initPixel: (pixelId) => {
      // Meta's standard install pattern is init followed by an explicit PageView track.
      // Init alone does not fire a PageView event in Events Manager unless Automatic
      // Events is enabled at the pixel level (deprecated for new pixels). trackSingle
      // scopes the PageView to the pixel just initialized, avoiding a duplicate fire
      // for any other pixel already on the page. The `as any` cast on init covers a
      // call shape not declared in global.d.ts's fbq type.
      ;(window as any).fbq?.('init', pixelId)
      window.fbq?.('trackSingle', pixelId, 'PageView', {})
    },
    setConsent: (action) => {
      window.fbq?.('consent', action)
    },
    track: (pixelId, event, payload) => {
      window.fbq?.(event.startsWith('C_') ? 'trackSingleCustom' : 'trackSingle', pixelId, event, payload)
    },
    removeFbScript: () => {
      scriptTag?.remove()
      scriptTag = null
    },
    isAdvertisingConsentGranted: realIsAdvertisingConsentGranted,
    whenTrustArcReady: realWhenTrustArcReady,
    onConsentChange: realOnConsentChange
  }
}

const defaultLoader = createMetaPixelLoader(makeDefaultDeps())

export const registerPixel = defaultLoader.registerPixel
